/* eslint-disable no-undef */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  CardActionArea,
  Button,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import { SEO } from '../../components/SEO/SEO';
import { BreadcrumbNav } from '../../components/common/BreadcrumbNav';
import { brandColors } from '../../theme';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';
import { useUserEntitlements } from '../../hooks/useUserEntitlements';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { 
  LocationOn as MarketIcon,
  Flag as CountryIcon,
  Public as ContinentIcon,
  ManageAccounts as ManageIcon,
  TrendingUp,
  Subscriptions as SubscriptionsIcon,
  PriorityHigh as PriorityHighIcon,
} from '@mui/icons-material';
import { API_URL } from '../../utils/config';

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [marketSearchTerm, setMarketSearchTerm] = useState('');
  const [trendReports, setTrendReports] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [groupingMarkets, setGroupingMarkets] = useState<any[]>([]);
  const [loadingGroupingMarkets, setLoadingGroupingMarkets] = useState(false);
  const hasInitializedTrendReports = useRef(false);
  const hasCustomizePermission = hasPermission('market_compass.customize');
  
  // Memoize filters to prevent infinite loop - include both active and expiring
  const filters = useMemo(() => ({}), []);
  
  const { 
    data: entitlementsData, 
    loading: entitlementsLoading, 
    error: entitlementsError 
  } = useUserEntitlements({
    filters,
    enabled: !!currentUser,
  });

  // Group entitlements by access level - only show active and expiring
  const validStatuses = ['active', 'expiring'];
  const marketEntitlements = entitlementsData?.entitlements.filter(e =>
    e.accessLevel === 'market' &&
    validStatuses.includes(e.status) &&
    e.productSubtype !== 'customization_license'
  ) || [];

  // Customization license entitlements
  const customizationEntitlements = useMemo(() =>
    entitlementsData?.entitlements.filter(e =>
      e.accessLevel === 'market' &&
      validStatuses.includes(e.status) &&
      e.productSubtype === 'customization_license'
    ) || [],
    [entitlementsData?.entitlements]
  );

  // Market grouping customization entitlements - memoize to prevent infinite loop
  const groupingCustomizationEntitlements = useMemo(() =>
    entitlementsData?.entitlements.filter(e =>
      e.accessLevel === 'market_grouping' &&
      validStatuses.includes(e.status) &&
      e.productSubtype === 'grouping_customization_license'
    ) || [],
    [entitlementsData?.entitlements]
  );
  
  // Sort market entitlements alphabetically by market name
  const sortedMarketEntitlements = useMemo(() => {
    return [...marketEntitlements].sort((a, b) => {
      const nameA = a.market?.name || '';
      const nameB = b.market?.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [marketEntitlements]);
  
  // Filter market entitlements based on search term
  const filteredMarketEntitlements = useMemo(() => {
    if (!marketSearchTerm.trim()) {
      return sortedMarketEntitlements;
    }
    
    const searchLower = marketSearchTerm.toLowerCase().trim();
    return sortedMarketEntitlements.filter(entitlement => {
      const marketName = entitlement.market?.name?.toLowerCase() || '';
      const countryName = entitlement.market?.countryName?.toLowerCase() || '';
      return marketName.includes(searchLower) || countryName.includes(searchLower);
    });
  }, [sortedMarketEntitlements, marketSearchTerm]);
  
  const countryEntitlements = entitlementsData?.entitlements.filter(e => 
    e.accessLevel === 'country' && validStatuses.includes(e.status)
  ) || [];
  const continentEntitlements = entitlementsData?.entitlements.filter(e =>
    e.accessLevel === 'continent' && validStatuses.includes(e.status)
  ) || [];

  // Group markets by their market grouping
  const marketsByGrouping = useMemo(() => {
    const grouped = new Map<number, { name: string; markets: any[] }>();

    groupingMarkets.forEach(market => {
      if (!grouped.has(market.groupingId)) {
        grouped.set(market.groupingId, {
          name: market.groupingName,
          markets: []
        });
      }
      grouped.get(market.groupingId)!.markets.push(market);
    });

    return Array.from(grouped.values());
  }, [groupingMarkets]);

  // Fetch Custom Reports only once when user is authenticated
  React.useEffect(() => {
    if (!currentUser || hasInitializedTrendReports.current) {
      return;
    }

    hasInitializedTrendReports.current = true;

    const fetchTrendReports = async () => {
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/store/trend-reports`,
          { method: 'GET' }
        );

        if (response?.ok) {
          const data = await response.json();
          setTrendReports(data.purchases || []);
        }
      } catch (error) {
        console.error('Error fetching Custom Reports:', error);
      } finally {
        setLoadingReports(false);
      }
    };

    fetchTrendReports();
  }, [currentUser, authenticatedFetch]);

  // Fetch markets from market groupings when grouping entitlements change
  React.useEffect(() => {
    if (!currentUser || groupingCustomizationEntitlements.length === 0) {
      setGroupingMarkets([]);
      return;
    }

    const fetchGroupingMarkets = async () => {
      setLoadingGroupingMarkets(true);
      try {
        const allMarkets: any[] = [];

        // Fetch markets for each grouping
        for (const entitlement of groupingCustomizationEntitlements) {
          if (!entitlement.marketGroupingId) continue;

          const response = await authenticatedFetch(
            `${API_URL}/api/store/market-groupings/${entitlement.marketGroupingId}`
          );

          if (response?.ok) {
            const data = await response.json();
            if (data.grouping?.markets) {
              // Add grouping info to each market for display
              const marketsWithGrouping = data.grouping.markets.map((market: any) => {
                // Map snake_case to camelCase if API hasn't been updated yet
                const normalizedMarket = {
                  ...market,
                  productImage2: market.productImage2 || market.product_image2,
                  productImage3: market.productImage3 || market.product_image3,
                  groupingName: data.grouping.name,
                  groupingId: data.grouping.id,
                  entitlementId: entitlement.id,
                  billingPeriod: entitlement.billingPeriod,
                  status: entitlement.status,
                  validUntil: entitlement.validUntil,
                  currentPeriodEnd: entitlement.currentPeriodEnd,
                };
                return normalizedMarket;
              });
              allMarkets.push(...marketsWithGrouping);
            }
          }
        }

        setGroupingMarkets(allMarkets);
      } catch (error) {
        console.error('Error fetching grouping markets:', error);
      } finally {
        setLoadingGroupingMarkets(false);
      }
    };

    fetchGroupingMarkets();
  }, [currentUser, groupingCustomizationEntitlements, authenticatedFetch]);

  // Handle hash changes
  useEffect(() => {
    // Check if hash indicates Custom Reports tab
    if (location.hash === '#trend-reports') {
      setTabValue(1);
    } else if (location.hash === '#customizer') {
      setTabValue(2);
    }
  }, [location.hash]);

  const handleManageSubscriptions = async () => {
    setIsLoadingPortal(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/store/customerPortal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response && response.ok) {
        const data = await response.json();
        if (data.portalUrl) {
          window.location.href = data.portalUrl;
        }
      } else {
        // Failed to get customer portal URL
      }
    } catch (_error) {
      void _error;
      // Failed to get customer portal URL
    } finally {
      setIsLoadingPortal(false);
    }
  };


  // No longer redirect to access-denied - just show empty subscriptions page

  // If user is not loaded yet, show loading
  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </Container>
    );
  }


  // Render market card for grouping markets
  const renderGroupingMarketCard = (market: any) => {
    if (!market) return null;

    const formatDate = (date: string | Date | undefined) => {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const formatBillingPeriod = (period?: string) => {
      if (!period) return 'N/A';
      return period.charAt(0).toUpperCase() + period.slice(1);
    };

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={`grouping-${market.groupingId}-${market.id}`}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            }
          }}
        >
          <CardActionArea
            onClick={() => navigate(`/market/${market.id}`)}
            sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
          >
            {market.productImage2 && (
              <Box
                component="img"
                src={market.productImage2}
                alt={market.name}
                sx={{
                  width: '100%',
                  height: 180,
                  objectFit: 'cover',
                  borderTopLeftRadius: 1,
                  borderTopRightRadius: 1,
                }}
              />
            )}
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div" gutterBottom>
                {market.name}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Grouping:</strong> {market.groupingName}
                </Typography>

                {market.country?.name && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong> {market.country.name}
                  </Typography>
                )}

                {market.billingPeriod && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Billing:</strong> {formatBillingPeriod(market.billingPeriod)}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  sx={{
                    color: market.status === 'expiring' ? "rgb(196, 22, 28)" : "text.secondary"
                  }}
                >
                  {(() => {
                    if (market.status === 'expiring') {
                      return <><strong>Expiring:</strong> {formatDate(market.validUntil)}</>;
                    }

                    if (market.status === 'active' && market.currentPeriodEnd) {
                      return <><strong>Renews:</strong> {formatDate(market.currentPeriodEnd)}</>;
                    }

                    if (!market.validUntil) {
                      return <><strong>Active:</strong> Lifetime</>;
                    }

                    return <><strong>Renews:</strong> {formatDate(market.validUntil)}</>;
                  })()}
                </Typography>
              </Box>

              {market.status === 'active' && (
                <Typography variant="caption" color="primary" sx={{ mt: 1.5, display: 'block' }}>
                  Click to customize market data
                </Typography>
              )}
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>
    );
  };

  const renderMarketCard = (entitlement: any) => {
    const market = entitlement.market;
    if (!market) return null;

    // Use product_image2 for customization licenses, product_image for regular subscriptions
    const isCustomizationLicense = entitlement.productSubtype === 'customization_license';
    const cardImage = isCustomizationLicense
      ? (market.productImage2 || '/images/market_customization_950x720.jpg')
      : market.productImage;

    const formatDate = (date: string | Date | undefined) => {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const formatBillingPeriod = (period?: string) => {
      if (!period) return 'N/A';
      return period.charAt(0).toUpperCase() + period.slice(1);
    };

    return (
    <Grid item xs={12} sm={6} md={4} lg={3} key={market.id}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          }
        }}
      >
        <CardActionArea
          onClick={() => navigate(`/market/${market.id}`)}
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          {cardImage && (
            <Box
              component="img"
              src={cardImage}
              alt={market.name}
              sx={{
                width: '100%',
                height: 180,
                objectFit: 'cover',
                borderTopLeftRadius: 1,
                borderTopRightRadius: 1,
              }}
            />
          )}
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" gutterBottom>
              {market.name}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              {market.countryName && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Location:</strong> {market.countryName}
                </Typography>
              )}
              
              {entitlement.billingPeriod && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Billing:</strong> {formatBillingPeriod(entitlement.billingPeriod)}
                </Typography>
              )}
              
              <Typography 
                variant="body2" 
                sx={{
                  color: entitlement.status === 'expiring' ? "rgb(196, 22, 28)" : "text.secondary"
                }}
              >
                {(() => {
                  if (entitlement.status === 'expiring') {
                    return <><strong>Expiring:</strong> {formatDate(entitlement.validUntil)}</>;
                  }
                  
                  if (entitlement.status === 'active' && entitlement.currentPeriodEnd) {
                    return <><strong>Renews:</strong> {formatDate(entitlement.currentPeriodEnd)}</>;
                  }
                  
                  if (!entitlement.validUntil) {
                    return <><strong>Active:</strong> Lifetime</>;
                  }
                  
                  return <><strong>Renews:</strong> {formatDate(entitlement.validUntil)}</>;
                })()}
              </Typography>
            </Box>
            
            {entitlement.status === 'active' && (
              <Typography variant="caption" color="primary" sx={{ mt: 1.5, display: 'block' }}>
                Click to access reports and podcasts
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
    );
  };

  return (
    <>
      <SEO
        title="My Subscriptions"
        description="Manage your HTL MarketCompass subscriptions. View active market subscriptions, customize reports, and access your market analysis products."
        robots="noindex, follow"
      />
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          {/* Breadcrumbs */}
          <BreadcrumbNav
            items={[
              { name: 'Home', url: '/' },
              { name: 'Dashboard', url: '/dashboard' },
              { name: 'Subscriptions' }
            ]}
          />

          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography
              component="h1"
              sx={{
                color: brandColors.blue,
                fontWeight: 400,
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
                lineHeight: 1.05,
              letterSpacing: '-0.5px',
            }}
          >
            Your Purchases
          </Typography>
        </Box>
        
        {/* Always show tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => {
              setTabValue(newValue);
              // Update URL hash based on selected tab
              if (newValue === 1) {
                window.location.hash = 'trend-reports';
              } else if (newValue === 2) {
                window.location.hash = 'customizer';
              } else {
                window.location.hash = '';
              }
            }}
            aria-label="purchase tabs"
          >
            <Tab
              label="MarketCompass Subscriptions"
              icon={<SubscriptionsIcon />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              label="Custom Reports"
              icon={<TrendingUp />}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            {hasCustomizePermission && (
              <Tab
                label="Market Customizer"
                icon={<ManageIcon />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            )}
          </Tabs>
        </Box>

        {/* MarketCompass Subscriptions Tab */}
        {tabValue === 0 && (
          <Box sx={{ mt: 3 }}>
            {entitlementsLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading your subscriptions...</Typography>
              </Box>
            ) : entitlementsError ? (
              <Alert severity="error">
                Failed to load subscriptions. Please try again later.
              </Alert>
            ) : (
              <>
                {entitlementsData?.entitlements.length === 0 && (
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      You don't have any active subscriptions yet.
                    </Typography>
                  </Paper>
                )}

            {/* Direct Market Subscriptions */}
            {marketEntitlements.length > 0 && (
              <Box sx={{ mt: 3 }}>
                {/* Search bar - show only when there are more than 8 market subscriptions */}
                {marketEntitlements.length > 8 && (
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search market subscriptions..."
                      value={marketSearchTerm}
                      onChange={(e) => setMarketSearchTerm(e.target.value)}
                      sx={{ maxWidth: 400 }}
                    />
                  </Box>
                )}
                
                <Grid container spacing={3}>
                  {filteredMarketEntitlements.map(entitlement => (
                    entitlement.market && renderMarketCard(entitlement)
                  ))}
                </Grid>
                
                {/* Show message when search has no results */}
                {marketSearchTerm && filteredMarketEntitlements.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No market subscriptions found matching "{marketSearchTerm}"
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Country-level Subscriptions */}
            {countryEntitlements.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CountryIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Country Subscriptions
                    </Typography>
                    <Chip 
                      label={countryEntitlements.length} 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Box>
                  {countryEntitlements.map(entitlement => (
                    <Box key={entitlement.id} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {entitlement.country?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Access to all markets in this country
                      </Typography>
                    </Box>
                  ))}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Country subscriptions give you access to all markets within the country. 
                    Visit the Market Reports page to explore available markets.
                  </Alert>
                </CardContent>
              </Card>
            )}

            {/* Continent-level Subscriptions */}
            {continentEntitlements.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ContinentIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Continent Subscriptions
                    </Typography>
                    <Chip 
                      label={continentEntitlements.length} 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Box>
                  {continentEntitlements.map(entitlement => (
                    <Box key={entitlement.id} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {entitlement.continent?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Access to all markets in this continent
                      </Typography>
                    </Box>
                  ))}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Continent subscriptions give you access to all markets within the continent. 
                    Visit the Market Reports page to explore available markets.
                  </Alert>
                </CardContent>
              </Card>
            )}

                {/* Manage Subscriptions Button */}
                {entitlementsData && entitlementsData.entitlements.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Button
                      variant="contained"
                      startIcon={<ManageIcon />}
                      onClick={handleManageSubscriptions}
                      disabled={isLoadingPortal}
                      fullWidth
                      size="large"
                    >
                      {isLoadingPortal ? 'Loading...' : 'Manage Subscriptions'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Custom Reports Tab */}
        {tabValue === 1 && (
          <Box sx={{ mt: 3 }}>

                {loadingReports ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : trendReports.length === 0 ? (
                  <Paper sx={{ p: 3 }}>
                    <Typography color="text.secondary">
                      No custom reports purchased yet
                    </Typography>
                  </Paper>
                ) : (
                <Grid container spacing={3}>
                  {trendReports.map((report) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={report.id}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                          }
                        }}
                      >
                        <CardActionArea
                          onClick={() => navigate(`/trendreport/${report.id}`)}
                          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                        >
                          {report.productImage2 && (
                            <Box
                              component="img"
                              src={report.productImage2}
                              alt={report.marketName}
                              sx={{
                                width: '100%',
                                height: 180,
                                objectFit: 'cover',
                                borderTopLeftRadius: 1,
                                borderTopRightRadius: 1,
                              }}
                            />
                          )}
                          <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" component="div" gutterBottom>
                              {report.marketName}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Type:</strong> Custom Comp Set Report
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary">
                                <strong>Purchased:</strong> {new Date(report.purchaseDate).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </Typography>
                              
                              <Box sx={{ mt: 1 }}>
                                <Chip 
                                  label={report.status.replace(/_/g, ' ').charAt(0).toUpperCase() + report.status.replace(/_/g, ' ').slice(1)} 
                                  color={
                                    report.status === 'purchased' ? 'error' :
                                    report.status === 'completed' ? 'success' :
                                    report.status === 'submitted' ? 'warning' :
                                    report.status === 'needs_adjustments' ? 'error' :
                                    'default'
                                  }
                                  size="small"
                                />
                              </Box>
                            </Box>
                            
                            {report.status === 'purchased' && (
                              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PriorityHighIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                <Typography variant="caption" color="error.main" sx={{ fontStyle: 'italic' }}>
                                  Additional information required
                                </Typography>
                              </Box>
                            )}
                            
                            {report.status === 'completed' && (
                              <Typography variant="caption" color="primary" sx={{ mt: 1.5, display: 'block' }}>
                                Click to view and download your report
                              </Typography>
                            )}
                            
                            {report.status === 'submitted' && (
                              <Typography variant="caption" color="info.main" sx={{ mt: 1.5, display: 'block' }}>
                                Your report is being processed
                              </Typography>
                            )}
                            
                            {report.status === 'needs_adjustments' && (
                              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PriorityHighIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                <Typography variant="caption" color="error.main">
                                  Adjustments needed - click for details
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                  </Grid>
                )}
              </Box>
            )}

        {/* Market Customizer Tab */}
        {tabValue === 2 && hasCustomizePermission && (
          <Box sx={{ mt: 3 }}>
            {entitlementsLoading || loadingGroupingMarkets ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading your customization licenses...</Typography>
              </Box>
            ) : customizationEntitlements.length === 0 && groupingMarkets.length === 0 ? (
              <Paper sx={{ p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  You don't have any active customization licenses yet.
                </Typography>
              </Paper>
            ) : (
              <>
                {/* Individual Market Customization Licenses */}
                {customizationEntitlements.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: brandColors.indigo }}>
                      Individual Market Licenses
                    </Typography>
                    <Grid container spacing={3}>
                      {customizationEntitlements.map(entitlement => (
                        entitlement.market && renderMarketCard(entitlement)
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Market Grouping Customization Licenses */}
                {marketsByGrouping.length > 0 && (
                  <Box>
                    {marketsByGrouping.map((grouping, index) => (
                      <Box key={grouping.name} sx={{ mb: index < marketsByGrouping.length - 1 ? 4 : 0 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: brandColors.indigo }}>
                          {grouping.name}
                          <Chip
                            label={`${grouping.markets.length} markets`}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </Typography>
                        <Grid container spacing={3}>
                          {grouping.markets.map(market => renderGroupingMarketCard(market))}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Container>
    </>
  );
};

export default SubscriptionsPage;